if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this._pinchStates = new WeakMap();
        this._pinchGesture = null;
        this._swipeGesture = null;
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this._pinchListenersBound) return;
        this._pinchListenersBound = true;
        this.addEventListener('touchstart', this.onTouchStart, { passive: true, capture: true });
        this.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true });
        this.addEventListener('touchend', this.onTouchEnd, { passive: true, capture: true });
        this.addEventListener('touchcancel', this.onTouchEnd, { passive: true, capture: true });
      }

      hide() {
        this.resetModalImageZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.resetModalImageZoom();
      }

      _getModalContent() {
        return this.querySelector('[role="document"]');
      }

      _getModalMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      _getActiveMedia() {
        return this._getModalMediaItems().find((item) => item.classList.contains('active'));
      }

      _showMediaById(id) {
        if (!id) return;
        const mediaItems = this._getModalMediaItems();
        if (!mediaItems.length) return;

        mediaItems.forEach((element) => {
          if (element.getAttribute('data-media-id') !== String(id)) element.classList.remove('active');
        });

        const activeMedia = mediaItems.find((item) => item.getAttribute('data-media-id') === String(id));
        if (!activeMedia) return;

        activeMedia.classList.add('active');
        activeMedia.scrollIntoView({ block: 'nearest' });

        const container = this._getModalContent();
        if (container) container.scrollLeft = 0;

        const deferredMediaElement =
          activeMedia.nodeName === 'DEFERRED-MEDIA' ? activeMedia : activeMedia.querySelector('deferred-media');
        const deferredTemplate = deferredMediaElement?.querySelector('template');
        if (deferredMediaElement && deferredTemplate?.content?.querySelector('.js-youtube')) {
          deferredMediaElement.loadContent();
        }
      }

      showActiveMedia() {
        const id = this.openedBy?.getAttribute('data-media-id');
        if (id) {
          this._showMediaById(id);
          return;
        }
        const firstMedia = this._getModalMediaItems()[0];
        if (firstMedia) this._showMediaById(firstMedia.getAttribute('data-media-id'));
      }

      showAdjacentMedia(direction) {
        const mediaItems = this._getModalMediaItems();
        if (mediaItems.length < 2) return;
        const activeMedia = this._getActiveMedia();
        const activeIndex = activeMedia ? mediaItems.indexOf(activeMedia) : 0;
        const normalizedActiveIndex = activeIndex < 0 ? 0 : activeIndex;
        const targetIndex = (normalizedActiveIndex + direction + mediaItems.length) % mediaItems.length;
        this._showMediaById(mediaItems[targetIndex].getAttribute('data-media-id'));
      }

      resetModalImageZoom() {
        this.querySelectorAll('.product-media-modal__image-wrap img').forEach((img) => {
          img.style.transform = '';
        });
        this.querySelectorAll('.product-media-modal__image-wrap').forEach((wrap) => {
          this._pinchStates.set(wrap, { scale: 1, posX: 0, posY: 0 });
        });
        this._pinchGesture = null;
        this._swipeGesture = null;
      }

      _pinchState(wrap) {
        if (!this._pinchStates.has(wrap)) {
          this._pinchStates.set(wrap, { scale: 1, posX: 0, posY: 0 });
        }
        return this._pinchStates.get(wrap);
      }

      _isMobilePinchTarget() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      _touchDistance(a, b) {
        return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      }

      _applyPinchTransform(img, state) {
        img.style.transform = `translate(${state.posX}px, ${state.posY}px) scale(${state.scale})`;
      }

      _clampPan(wrap, img, state) {
        if (state.scale <= 1) {
          state.posX = 0;
          state.posY = 0;
          return;
        }
        const prev = img.style.transform;
        img.style.transform = '';
        const baseW = img.offsetWidth;
        const baseH = img.offsetHeight;
        img.style.transform = prev;

        const maxX = Math.max(0, (state.scale * baseW - wrap.clientWidth) / 2);
        const maxY = Math.max(0, (state.scale * baseH - wrap.clientHeight) / 2);
        state.posX = Math.min(maxX, Math.max(-maxX, state.posX));
        state.posY = Math.min(maxY, Math.max(-maxY, state.posY));
      }

      onTouchStart(event) {
        if (!this.hasAttribute('open') || !this._isMobilePinchTarget()) return;
        const activeMedia = this._getActiveMedia();
        if (!activeMedia || !activeMedia.contains(event.target)) return;

        const wrap = event.target.closest('.product-media-modal__image-wrap');
        if (wrap && !this.contains(wrap)) return;
        const img = wrap ? wrap.querySelector('img') : null;
        const state = wrap ? this._pinchState(wrap) : { scale: 1, posX: 0, posY: 0 };

        if (event.touches.length === 2) {
          if (!wrap || !img) return;
          this._pinchGesture = {
            wrap,
            mode: 'pinch',
            initialDistance: Math.max(1, this._touchDistance(event.touches[0], event.touches[1])),
            initialScale: state.scale,
          };
          this._swipeGesture = null;
        } else if (event.touches.length === 1 && wrap && state.scale > 1.01) {
          this._pinchGesture = {
            wrap,
            mode: 'pan',
            startX: event.touches[0].clientX - state.posX,
            startY: event.touches[0].clientY - state.posY,
          };
          this._swipeGesture = null;
        } else if (event.touches.length === 1) {
          this._swipeGesture = {
            startX: event.touches[0].clientX,
            startY: event.touches[0].clientY,
            deltaX: 0,
            deltaY: 0,
            horizontal: false,
          };
        }
      }

      onTouchMove(event) {
        if (!this.hasAttribute('open') || !this._isMobilePinchTarget()) return;

        if (this._pinchGesture) {
          const { wrap, mode } = this._pinchGesture;
          if (!this.contains(wrap)) {
            this._pinchGesture = null;
            return;
          }

          const img = wrap.querySelector('img');
          if (!img) return;

          const state = this._pinchState(wrap);

          if (mode === 'pinch' && event.touches.length === 2) {
            event.preventDefault();
            const d = this._touchDistance(event.touches[0], event.touches[1]);
            const ratio = d / this._pinchGesture.initialDistance;
            state.scale = Math.min(4, Math.max(1, this._pinchGesture.initialScale * ratio));
            this._clampPan(wrap, img, state);
            this._applyPinchTransform(img, state);
          } else if (mode === 'pan' && event.touches.length === 1 && state.scale > 1) {
            event.preventDefault();
            state.posX = event.touches[0].clientX - this._pinchGesture.startX;
            state.posY = event.touches[0].clientY - this._pinchGesture.startY;
            this._clampPan(wrap, img, state);
            this._applyPinchTransform(img, state);
          }
          return;
        }

        if (!this._swipeGesture || event.touches.length !== 1) return;
        this._swipeGesture.deltaX = event.touches[0].clientX - this._swipeGesture.startX;
        this._swipeGesture.deltaY = event.touches[0].clientY - this._swipeGesture.startY;

        if (
          Math.abs(this._swipeGesture.deltaX) > Math.abs(this._swipeGesture.deltaY) &&
          Math.abs(this._swipeGesture.deltaX) > 12
        ) {
          this._swipeGesture.horizontal = true;
          event.preventDefault();
        }
      }

      onTouchEnd(event) {
        if (!this.hasAttribute('open') || !this._isMobilePinchTarget()) return;

        if (this._pinchGesture) {
          if (event.touches.length === 1 && this._pinchGesture.mode === 'pinch') {
            const wrap = this._pinchGesture.wrap;
            const state = this._pinchState(wrap);
            if (state.scale > 1) {
              this._pinchGesture = {
                wrap,
                mode: 'pan',
                startX: event.touches[0].clientX - state.posX,
                startY: event.touches[0].clientY - state.posY,
              };
            } else {
              this._pinchGesture = null;
            }
            return;
          }

          if (event.touches.length === 0) {
            const wrap = this._pinchGesture.wrap;
            this._pinchGesture = null;
            if (!wrap || !this.contains(wrap)) return;
            const state = this._pinchState(wrap);
            if (state.scale < 1.05) {
              state.scale = 1;
              state.posX = 0;
              state.posY = 0;
              const img = wrap.querySelector('img');
              if (img) this._applyPinchTransform(img, state);
            }
          }
          return;
        }

        if (this._swipeGesture && event.touches.length === 0) {
          const { deltaX, deltaY, horizontal } = this._swipeGesture;
          this._swipeGesture = null;
          if (!horizontal) return;

          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 45) {
            this.showAdjacentMedia(deltaX < 0 ? 1 : -1);
          }
        }
      }
    }
  );
}
